import { useEffect, useRef, type CSSProperties } from 'react';
import PageBack from '../components/PageBack';
import {
  getUseCase,
  getUseCaseSiblings,
  isPreviewRender,
} from '../../../shared-web/site-content-runtime';
import { collectHref } from '../lib/collectCta';
import { hydrateCtaLinks } from '../../../shared-web/cta-targets';
import DraftPreviewBanner from '../components/DraftPreviewBanner';
import AuthoredPage from '../components/AuthoredPage';

/**
 * Which site chrome an authored page asks for.
 *
 * Mirrors CHROME_DARK_MARKER / chromeThemeFor in
 * src/edge/content/page-html.js. Duplicated rather than imported because the
 * marketing bundle must not pull an edge module into the browser; pinned by a
 * test on the edge side.
 */
function chromeThemeFor(html?: string): 'light' | 'dark' {
  return String(html || '').includes('data-ember-chrome="dark"') ? 'dark' : 'light';
}

/**
 * The authored body's own content column, so the Back row can stand on it.
 *
 * Every other authored page in this bundle uses `.authored-top-wrap`, which
 * hardcodes the one width those deliveries share. The /for/ pages cannot: each
 * delivery brings its own column, and a fixed width lands the Back arrow 176px
 * inside the text on a wide one (prod 2026-09-15, /for/someone-to-talk-to at
 * 1120px against a 768px row). So read the width the page itself declares.
 *
 * Two shapes ship today, and they differ only in the gutter:
 *   `.wrap{max-width:var(--max);padding:0 24px}`   — the 2026-08 deliveries
 *   `width:min(var(--max),calc(100% - 40px))`      — the 2026-09 ones
 * Returning null (no `--max` declared) leaves the row on the Tailwind default
 * it has always had, which is what the two pages without one still want.
 */
function contentColumnFor(html?: string): CSSProperties | null {
  const source = String(html || '');
  const max = (source.match(/--max:\s*(\d+px)/) || [])[1];
  if (!max) return null;
  return /\.wrap\s*\{[^}]*padding:\s*0\s+24px/.test(source)
    ? { maxWidth: max, marginInline: 'auto', paddingInline: '24px' }
    : { width: `min(${max}, calc(100% - 40px))`, marginInline: 'auto' };
}


/**
 * One situation page: /for/a-breakup, /for/venting-about-work.
 *
 * The visitor arriving here searched their circumstances, not Ember's name, so
 * the page leads with the situation and reaches the product last. Content comes
 * from the database via the edge (window.__EMBER_CONTENT__ / the SSR override),
 * which is what lets ops publish a new one without a deploy.
 *
 * The body is injected as HTML. It was rendered from markdown on the edge by a
 * renderer that cannot emit raw HTML or an executable URL — see
 * src/edge/content/markdown.js, which explains why that is safe and why the
 * safety must not be weakened, given this origin holds the session cookie.
 */
export default function UseCasePage() {
  const page = getUseCase();
  const siblings = getUseCaseSiblings();
  const preview = isPreviewRender();
  const bodyRef = useRef<HTMLDivElement>(null);

  // The body is server-rendered HTML, so its `ember:` links arrive as plain
  // hrefs that work but carry no attribution. Upgrade them once the client can
  // compute it. Runs after every body change (a soft navigation between two
  // use-case pages replaces the markup without remounting).
  useEffect(() => {
    hydrateCtaLinks(bodyRef.current);
  }, [page?.slug, page?.body_html]);

  // No bootstrap: the edge 404s an unknown slug before it reaches a render, so
  // this is a client-side arrival at a page whose data never came. Say so
  // plainly rather than painting an empty template.
  if (!page) {
    return (
      <div className="flex min-h-screen flex-col bg-[#f9f5e9] text-ink">
        <div className="eh-page-top mx-auto w-full max-w-3xl px-6 sm:px-10">
          <PageBack fallback="/ways-to-use-ember" />
        </div>
        <div
          className="mx-auto w-full max-w-3xl flex-1 px-6 py-24 text-center sm:px-10"
          data-log-seen="page_failure"
          data-log-state="not_here"
        >
          <h1 className="mb-4 text-3xl font-bold" style={HEADING_FONT}>
            This page is not here
          </h1>
          <p className="mb-8 text-ink/80">It may have been retired, or the link may be wrong.</p>
          <a className="text-oriOrange underline" href="/ways-to-use-ember">
            See ways to use Ember
          </a>
        </div>
      </div>
    );
  }

  // The page brought its own layout. Everything below the page-level Back is
  // the author's; see docs/content-html-contract.md for the division of labour
  // and why the Back row and consent banner stay ours.
  if (page.page_html) {
    // The page's own CSS is unconditional; the chrome otherwise follows the
    // visitor's OS, so a dark page met a white header on a light-mode visitor.
    const chrome = chromeThemeFor(page.page_html);
    // Back on the body's own left edge, Home on its right — the same row the
    // other authored pages get from .authored-top-wrap, sized per delivery.
    const column = contentColumnFor(page.page_html);
    // The shell colour follows too: it shows above and below a page that does
    // not fill the viewport, and cream framing a near-black page reads as a
    // rendering fault.
    return (
      <div className={`flex min-h-screen flex-col text-ink ${chrome === 'dark' ? 'bg-[#14110e]' : 'bg-[#f9f5e9]'}`}>
        {preview && <DraftPreviewBanner />}
        <div
          className={column ? 'eh-page-top w-full' : 'eh-page-top mx-auto w-full max-w-3xl px-6 sm:px-10'}
          style={column || undefined}
        >
          <PageBack fallback="/ways-to-use-ember" theme={chrome} />
        </div>
        <AuthoredPage html={page.page_html} />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-[#f9f5e9] text-ink">
      {preview && <DraftPreviewBanner />}

      <article className="eh-page-top mx-auto w-full max-w-3xl flex-1 px-6 pb-16 sm:px-10">
        <div className="eh-page-back-row">
          <PageBack fallback="/ways-to-use-ember" />
        </div>
        {/* Breadcrumb: the visible half of the BreadcrumbList the edge emits as
            JSON-LD. Schema without the matching on-page trail is the kind of
            mismatch Google discounts, so the two ship together. */}
        <nav className="mb-6 text-sm text-ink/60" aria-label="Breadcrumb">
          <a className="hover:underline" href="/">Ember</a>
          <span className="mx-2" aria-hidden="true">/</span>
          <a className="hover:underline" href="/ways-to-use-ember">Ways to use Ember</a>
        </nav>

        <header className="mb-10">
          <h1 className="mb-3 text-4xl font-bold tracking-tight" style={HEADING_FONT}>
            {page.title}
          </h1>
          {page.hero_subtitle && (
            <p className="text-lg leading-relaxed text-ink/80" style={BODY_FONT}>
              {page.hero_subtitle}
            </p>
          )}
        </header>

        {page.image_url && (
          <img
            className="mb-10 w-full rounded-xl object-cover"
            src={page.image_url}
            alt={`${page.title} — illustration`}
            loading="lazy"
          />
        )}

        <div
          ref={bodyRef}
          className="space-y-4 text-base leading-relaxed text-ink/90 [&_a]:text-oriOrange [&_a]:underline [&_blockquote]:border-l-2 [&_blockquote]:border-ink/20 [&_blockquote]:pl-4 [&_blockquote]:italic [&_h2]:mb-3 [&_h2]:mt-10 [&_h2]:text-2xl [&_h2]:font-bold [&_h3]:mt-6 [&_h3]:font-semibold [&_li]:my-1 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:my-4 [&_ul]:list-disc [&_ul]:pl-6"
          style={BODY_FONT}
          // Safe by construction, not by trust — see the component header.
          dangerouslySetInnerHTML={{ __html: page.body_html }}
        />

        <section className="mt-14 rounded-xl bg-white/60 p-8 text-center">
          <h2 className="mb-3 text-2xl font-bold" style={HEADING_FONT}>
            You can start right now
          </h2>
          <p className="mb-6 text-ink/80" style={BODY_FONT}>
            No account to begin. Say as much or as little as you want.
          </p>
          <a
            className="eh-primary-button"
            href={collectHref()}
            data-log={`use_case_cta_${page.slug}`}
            data-log-seen={`use_case_cta_${page.slug}`}
          >
            <span>Talk to Ember</span>
          </a>
        </section>

        {/* Sibling links are structural, not decorative: a cluster of related
            situation pages linking to each other is most of what lets any of
            them rank. Built in so it cannot be forgotten in the body copy. */}
        {siblings.length > 0 && (
          <nav className="mt-14 border-t border-ink/10 pt-8" aria-label="Related situations">
            <h2 className="mb-4 text-lg font-semibold" style={HEADING_FONT}>
              Other things people bring here
            </h2>
            <ul className="space-y-3">
              {siblings.map((sibling) => (
                <li key={sibling.slug}>
                  <a
                    className="text-oriOrange underline"
                    href={`/for/${sibling.slug}`}
                    data-log={`use_case_sibling_${sibling.slug}`}
                    data-log-seen={`use_case_sibling_${sibling.slug}`}
                  >
                    {sibling.title}
                  </a>
                  {sibling.excerpt && (
                    <p className="mt-1 text-sm text-ink/70" style={BODY_FONT}>{sibling.excerpt}</p>
                  )}
                </li>
              ))}
            </ul>
          </nav>
        )}

        <p className="mt-10 text-sm">
          <a className="text-oriOrange underline" href="/ways-to-use-ember" data-log="use_case_more">
            See all the ways to use Ember
          </a>
        </p>
      </article>
    </div>
  );
}

const HEADING_FONT = {
  fontFamily: '"Cormorant Garamond", Georgia, serif',
  color: '#3A331D',
} as const;

const BODY_FONT = {
  fontFamily: '"STK Bureau Sans", Inter, system-ui, sans-serif',
} as const;
